import { ChangeDetectionStrategy, Component, computed, effect, inject, input, Signal, ViewEncapsulation } from '@angular/core';
import { TariffSize, TariffStore, TariffType } from '../../../../tariff';
import { TariffPricePipe, IndexToTariffSizePipe, IndexToTariffEntryPipe, FilterTariffSectionForSizePipe } from './pipes';
import { NgTemplateOutlet } from '@angular/common';
import { TariffSizeButton } from './size-button';
import { RouterLink } from '@angular/router';
import { CachedUrlResolverPipe } from '../../../pipes';
import { GtmService } from '@seven1/angular/gtm';
import { TailwindService } from '@seven1/angular-storyblok/tailwind';
import { ITariffTableBlok } from './tariff.table.model';
import { TariffSection } from './section';
import { BreakpointsService } from '@seven1/angular/media';
import { toSignal } from '@angular/core/rxjs-interop';
import { RouterLinkPipe } from '@seven1/angular-storyblok-components/cta';

export type TariffTableAppearance = '3-columns' | '4-columns';

@Component({
    selector: 'app-tariff-table',
    imports: [
        TariffPricePipe,
        IndexToTariffSizePipe,
        IndexToTariffEntryPipe,
        FilterTariffSectionForSizePipe,
        NgTemplateOutlet,
        TariffSizeButton,
        RouterLink,
        CachedUrlResolverPipe,
        TariffSection,
        RouterLinkPipe,
    ],
    templateUrl: './tariff.table.html',
    styleUrl: './tariff.table.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: {
        class: 'max-w-content-width mx-auto p-6',
        '[class]': 'getClasses()',
        '[class.md:gap-x-4]': '(responsiveBlok().appearance || "3-columns") === "3-columns"',
        '[id]': 'block().id',
    },
})
export class TariffTable {
    private _gtm = inject(GtmService);
    private _tailwind = inject(TailwindService);
    private _breakpoints = inject(BreakpointsService);
    private _minMd = toSignal(this._breakpoints.minMd$);
    readonly baseClass = 'app-tariff-table';
    tariffStore: TariffStore = inject(TariffStore);

    block = input.required<ITariffTableBlok>();

    responsiveBlok: Signal<ITariffTableBlok> = computed(() => {
        const minTablet = !!this._minMd()?.matches;
        const blok = this.block();
        if (minTablet) {
            return blok;
        } else {
            return { ...blok, appearance: '3-columns' };
        }
    });

    getClasses = computed<string[]>(() => {
        const block = this.responsiveBlok();
        return [
            'grid',
            'app-tariff-table-scroll-link',
            this.baseClass,
            this.baseClass + '-' + (block.appearance || '3-columns'),
            ...this._tailwind.getDisplayClasses(block, 'grid'),
        ];
    });

    constructor() {
        effect(() => {
            const block = this.responsiveBlok();
            if (block.topsellerTariff) {
                this.tariffStore.size = block.topsellerTariff;
            }
        });
    }

    selectTariff(event: MouseEvent | KeyboardEvent | TouchEvent, type: TariffType, size: TariffSize) {
        const mapping = this.tariffStore.getMapping(type, size);
        if (mapping) {
            this._gtm.logEvent('add_to_cart', undefined, 'add_to_cart', undefined, {
                ecommerce: {
                    items: [
                        {
                            item_id: mapping.key,
                            item_category: mapping.type,
                            item_variant: mapping.size,
                            item_name: mapping.name,
                            item_brand: 'ARAG',
                        },
                    ],
                },
            });
        }
    }

    // @Input({ required: true })
    // public appearance: TariffTableAppearance = '3-columns';
}
